import { Alert, Button, Select, Skeleton } from '@mantine/core';
import { IconAlertCircle, IconRefresh, IconShieldCheck } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { Panel } from '../../Panel.js';
import { CardFooter, settingsClasses } from './SettingsLayout.js';

/** Şirketin kaptan vekili ayarı (ADR 0127 §3). */
export interface MasterDeputySettings {
  /** Vekil rol; `null`: vekil yok, yalnız kaptan. */
  roleId: string | null;
  /** Ayar satırının sürümü; satır yoksa `null`. */
  rowVersion: number | null;
  /** Seçilebilen gemi rolleri (kaptan ve ofis rolleri dışında). */
  roles: { id: string; name: string }[];
}

export interface MasterDeputySettingsSource {
  load(): Promise<MasterDeputySettings>;
  /** Yoksa salt okunur ("Ayarlar / düzenleme" yetkisi yok). */
  save?: (rowVersion: number | null, roleId: string | null) => Promise<MasterDeputySettings>;
}

export interface MasterDeputySettingsPanelProps {
  /** `null`: okunuyor ya da okunamadı. */
  settings: MasterDeputySettings | null;
  error?: boolean;
  onRetry?: () => void;
  onSave?: (rowVersion: number | null, roleId: string | null) => Promise<void>;
}

const NONE = '';

/**
 * Ayarlar ▸ Kaptanın vekili (ADR 0127 §3): kaptana ait bölümleri (arıza Part B, HSEQ'de kaptanın
 * ilk değerlendirmesi, onarımın reddi) gemide kaptan ataması yokken "on behalf of Master" yazan
 * rütbe. Varsayılan yok: yalnız kaptan. Web ofiste düzenlenir; gemiye senkronla iner.
 */
export function MasterDeputySettingsPanel({
  settings,
  error,
  onRetry,
  onSave,
}: MasterDeputySettingsPanelProps) {
  const { t } = useTranslation();
  const k = (key: string) => t(`masterSection.settings.${key}`);
  const [value, setValue] = useState<string>(settings?.roleId ?? NONE);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (settings) setValue(settings.roleId ?? NONE);
  }, [settings]);
  const body = (() => {
    if (error) {
      return (
        <EmptyState
          tone="danger"
          title={t('common.loadError')}
          action={
            onRetry ? (
              <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={onRetry}>
                {k('retry')}
              </Button>
            ) : undefined
          }
        />
      );
    }
    if (!settings) {
      return (
        <div aria-busy="true">
          <Skeleton height={18} mb={10} />
          <Skeleton height={18} />
        </div>
      );
    }
    const current = settings.roleId ?? NONE;
    return (
      <>
        <p className={settingsClasses.intro}>{k('text')}</p>
        <Select
          label={k('role')}
          data={[
            { value: NONE, label: k('none') },
            ...settings.roles.map((role) => ({ value: role.id, label: role.name })),
          ]}
          value={value}
          allowDeselect={false}
          disabled={!onSave || busy}
          onChange={(next) => {
            setSaved(false);
            setFailure(false);
            setValue(next ?? NONE);
          }}
        />
        {failure ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert" mt="sm">
            {k('error')}
          </Alert>
        ) : saved ? (
          <Alert color="green" role="status" mt="sm">
            {k('saved')}
          </Alert>
        ) : null}
        {onSave ? (
          <CardFooter>
            <Button
              loading={busy}
              disabled={value === current}
              onClick={async () => {
                setBusy(true);
                setFailure(false);
                try {
                  await onSave(settings.rowVersion, value === NONE ? null : value);
                  setSaved(true);
                } catch {
                  setFailure(true);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {k('save')}
            </Button>
          </CardFooter>
        ) : null}
      </>
    );
  })();
  return (
    <Panel title={k('title')} icon={<IconShieldCheck size={18} stroke={1.8} />} divider>
      {body}
    </Panel>
  );
}

/** Kaynağa bağlı bölüm: okur, kaydeder, kaydedileni gösterir. */
export function MasterDeputySettingsSection({ source }: { source: MasterDeputySettingsSource }) {
  const [settings, setSettings] = useState<MasterDeputySettings | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` yeniden okutur
  useEffect(() => {
    let live = true;
    setError(false);
    source
      .load()
      .then((value) => {
        if (live) setSettings(value);
      })
      .catch(() => {
        if (live) setError(true);
      });
    return () => {
      live = false;
    };
  }, [source, attempt]);
  const save = source.save;
  return (
    <MasterDeputySettingsPanel
      settings={settings}
      error={error}
      onRetry={() => setAttempt((n) => n + 1)}
      {...(save
        ? {
            onSave: async (rowVersion: number | null, roleId: string | null) => {
              setSettings(await save(rowVersion, roleId));
            },
          }
        : {})}
    />
  );
}
